import type { ReactNode } from "react";
import { GENRE_OPTIONS, YEAR_OPTIONS, SORT_OPTIONS } from "@/mocks/movies";

interface FilterSidebarProps {
  genre: string;
  year: string;
  sort: string;
  onGenreChange: (value: string) => void;
  onYearChange: (value: string) => void;
  onSortChange: (value: string) => void;
  onReset: () => void;
}

function FilterGroup({
  label,
  icon,
  children,
}: {
  label: string;
  icon: string;
  children: ReactNode;
}) {
  return (
    <div className="border-b border-background-200 pb-5 last:border-0 last:pb-0">
      <h4 className="mb-3 flex items-center gap-2 text-sm font-bold text-foreground-900">
        <i className={`${icon} text-base text-accent-500`} />
        {label}
      </h4>
      {children}
    </div>
  );
}

export default function FilterSidebar({
  genre,
  year,
  sort,
  onGenreChange,
  onYearChange,
  onSortChange,
  onReset,
}: FilterSidebarProps) {
  return (
    <div className="space-y-5 rounded-lg border border-background-200 bg-background-50 p-5">
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
          <i className="ri-equalizer-line text-lg text-primary-600" />
          筛选条件
        </span>
        <button
          type="button"
          onClick={onReset}
          className="whitespace-nowrap text-xs font-medium text-foreground-500 transition-colors hover:text-accent-600"
        >
          重置
        </button>
      </div>

      <FilterGroup label="题材类型" icon="ri-price-tag-3-line">
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => onGenreChange("all")}
            className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
              genre === "all"
                ? "bg-primary-500 text-background-50"
                : "bg-secondary-100 text-secondary-900 hover:bg-secondary-200"
            }`}
          >
            全部
          </button>
          {GENRE_OPTIONS.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => onGenreChange(item)}
              className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                genre === item
                  ? "bg-primary-500 text-background-50"
                  : "bg-secondary-100 text-secondary-900 hover:bg-secondary-200"
              }`}
            >
              {item}
            </button>
          ))}
        </div>
      </FilterGroup>

      <FilterGroup label="上线年份" icon="ri-calendar-line">
        <div className="flex flex-wrap gap-2">
          {YEAR_OPTIONS.map((item) => (
            <button
              key={item.value}
              type="button"
              onClick={() => onYearChange(item.value)}
              className={`whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                year === item.value
                  ? "bg-accent-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-background-200"
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
      </FilterGroup>

      <FilterGroup label="排序方式" icon="ri-sort-desc">
        <div className="flex flex-col gap-2">
          {SORT_OPTIONS.map((item) => (
            <button
              key={item.value}
              type="button"
              onClick={() => onSortChange(item.value)}
              className={`flex items-center justify-between rounded-md px-3 py-2 text-xs font-medium transition-colors ${
                sort === item.value
                  ? "bg-primary-100 text-primary-700"
                  : "bg-background-100 text-foreground-700 hover:bg-background-200"
              }`}
            >
              {item.label}
              {sort === item.value && <i className="ri-check-line text-sm" />}
            </button>
          ))}
        </div>
      </FilterGroup>
    </div>
  );
}